import React from "react";

/**
 * Full-viewport loading indicator used by the tagguid ownership-transfer
 * pages (OwnershipApprovalPage, OwnershipNomineePage) while the request
 * details are being fetched, or while the approve/reject action is being
 * applied.
 *
 * IMPORTANT: uses ONLY inline styles + a single inline <style> tag for
 * the spin keyframe -- deliberately no dependency on:
 *   - external CSS chunks (OwnershipApprovalPage.css may still be loading
 *     when the component first mounts on a cold cache)
 *   - Nike UI components like <Spinner/> from @nike/epic-react-ui (may
 *     ship its own async CSS chunk)
 *
 * Prod users reported that the ownership pages rendered as blank white
 * on first click of the email link (worked after reload). Root cause was
 * that the initial loading JSX used `className="ownership-page"` etc.
 * which reference OwnershipApprovalPage.css -- if that CSS chunk hadn't
 * finished loading yet, the container had no styling and the Spinner
 * ended up invisible / positioned off-screen. This component renders
 * visibly the instant React mounts it, regardless of CSS chunk state.
 */
const OwnershipLoader = ({ label, sublabel }) => (
  <div
    role="status"
    aria-label={label || "Loading"}
    style={{
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#ffffff",
      color: "#4a4a4a",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
      zIndex: 9999
    }}
  >
    {/*
      Inline @keyframes -- React renders <style> tags in JSX correctly.
      Prefixed with `ownership-loader-` so it can't collide with anything
      else on the page.
    */}
    <style>{`
      @keyframes ownership-loader-spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
    <div
      aria-hidden="true"
      style={{
        width: "56px",
        height: "56px",
        border: "5px solid #f2c2c2",
        borderTopColor: "#a40000",
        borderRadius: "50%",
        animation: "ownership-loader-spin 0.9s linear infinite"
      }}
    />
    <div
      style={{
        marginTop: "20px",
        fontSize: "15px",
        letterSpacing: "0.02em",
        color: "#4a4a4a"
      }}
    >
      {label || "Loading…"}
    </div>
    {sublabel && (
      <div
        style={{
          marginTop: "6px",
          fontSize: "12px",
          color: "#8a8a8a"
        }}
      >
        {sublabel}
      </div>
    )}
  </div>
);

export default OwnershipLoader;
